import { Component, ElementRef, HostListener, Input, ViewChild } from '@angular/core';
import { combineLatest, debounceTime, fromEvent, map, Observable, startWith, tap } from 'rxjs';

import { ControlZoom } from 'ChartApp/ts/interfaces/chart-common.interface';

import { ChartZoomService } from 'ChartApp/ts/features/charts-shared/services/chart-zoom.service';

/**
 * ChartZoomControlsComponent
 *
 * A component housing zoom related Highcharts functionality.
 * This includes the zoom-in, reset-zoom, and panning behavior.
 *
 * Inputs:
 * top: numeric top position in pixels for zoom controls position
 * hideZoomInstructions: hide the zoom control instruction box
 * mouseOverChart: A boolean describing whether the user's mouse is hovering over the chart. This is used to prevent
 *   enabling zoom when the user presses `shift` on a different part of the site ex: Typing a capital letter in a
 *   search bar.
 */
@Component({
    standalone: false,
    selector: 'ycn-chart-zoom-controls',
    templateUrl: './chart-zoom-controls.template.html',
})
export class ChartZoomControlsComponent {
    @Input() mouseOverChart!: boolean;

    @Input() top: number = 0;

    @Input() hideZoomInstructions: boolean = false;

    @ViewChild('panningButton') panningButton!: ElementRef;

    @ViewChild('controls') zoomControl!: ElementRef;

    enablePanning: boolean = false;

    zoomOn: boolean = false;

    visible: string = 'hidden';

    chart$: Observable<ControlZoom> = this.chartZoomService.getZoomEvent().pipe(
        tap(({ zoomedIn, panningOn }) => {
            this.zoomOn = zoomedIn;
            this.enablePanning = panningOn;
        })
    );

    private enablePanningEventType?: string;

    chartLeft$: Observable<string> = combineLatest([
        fromEvent(window, 'resize').pipe(startWith({ target: window })),
        this.chartZoomService.getChartWidthChange(),
    ]).pipe(
        debounceTime(500),
        map(([_, plotChartPadding]) => {
            const zoomWidth = this.zoomControl.nativeElement.offsetWidth;
            const rightPadding = plotChartPadding + zoomWidth - 1; // 1 pixel from the chart border
            this.visible = 'visible';
            return `max(66.66%, 100% - ${rightPadding}px)`; // Zoom Controls should not go inside legend's content
        })
    );

    constructor(private chartZoomService: ChartZoomService) {}

    resetZoomHandler(): void {
        this.enablePanningEventType = undefined;
        this.chartZoomService.zoomOut();
        if (this.enablePanning) {
            this.chartZoomService.updatePanning(false);
        }
    }

    /**
     * Toggle between panning and zooming on the highchart by clicking on the Move icon or holding shift.
     * Only allow users to disable panning with the same event used to enable panning.
     * @param event - A click, keydown.shift, or keyup.shift event.
     */
    @HostListener('document:keydown.shift', ['$event'])
    @HostListener('document:keyup.shift', ['$event'])
    panningHandler(event: Event): void {
        const panningEventType = event.type.includes('key') ? 'key' : 'click';
        if (
            !this.zoomOn ||
            (this.enablePanningEventType && panningEventType !== this.enablePanningEventType) ||
            (panningEventType === 'key' && !this.mouseOverChart)
        ) {
            return;
        }

        event.preventDefault();
        const enablePanning = panningEventType === 'key' ? event.type === 'keydown' : !this.enablePanning;
        this.enablePanningEventType = enablePanning ? panningEventType : undefined;
        this.chartZoomService.updatePanning(enablePanning);
        if (!enablePanning) {
            this.panningButton.nativeElement.blur();
        }
    }
}
